import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { CellValueChangedEvent } from 'ag-grid-community';
import DialogUsers from '@/components/DialogUsers/DialogUsers';
import { IcoButtonSimple } from '@/components/UI/IcoButton/IcoButton';
import SimpleButton from '@/components/UI/SimpleButton/SimpleButton';
import { ALIGN, CELL_EDITOR_TYPES, CELL_RENDERER_TYPES } from '@/components/UI/TableListEditable/TableListEditableConstants';
import TableListEditableNew from '@/components/UI/TableListEditable/TableListEditableNew';
import { ERROR_TYPES_ADD_STANDIN, errorRoleToFormatedRole } from '@/helpers/constants';
import { backFormatRoleToCleanFormat, formatSummaryUser } from '@/helpers/utils';
import useApiRequest from '@/hooks/useApiRequest';
import useAppLanguage from '@/hooks/useAppLanguage';
import useAuth from '@/hooks/useAuth';
import useToastContext from '@/hooks/useToastContext';
import { getFiltersConfig } from '@/redux/OverviewFilters/overviewFilters.reducer';
import { setStandInFirstTimeResalted, setUserStandIns } from '@/redux/User/user.action';
import { getSettingsInfo, getStandInFirstTimeResalted, getStandins, getUserAll } from '@/redux/User/user.reducer';
import titleService from '@/services/titleService';
import usersServices from '@/services/usersServices';
import { ColumnConfig, UserStandinComplete } from '@/types';
import styles from './StandIn.module.scss';

interface StandInProps {
  userInfo: any;
  settingsInfo: any;
  standInResalted: boolean;
  filtersConfig: any;
  setSettings?: (settings: any) => void;
  setStandInResalted: (resalted: boolean) => void;
  setUserStandInsR: (standIns: any) => void;
  userStandIns: UserStandinComplete[];
}

const StandIn: React.FC<StandInProps> = (props) => {
  const { standInResalted, setStandInResalted, userInfo, filtersConfig, userStandIns, setUserStandInsR } = props;
  const { t } = useTranslation();
  const lang = useAppLanguage();
  const authService = useAuth();
  const { addToast } = useToastContext();
  const [isNewStandInOpen, setNewStandInOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selection, setSelection] = useState<string[]>([]);

  const tableFormat = (allResults: any[]) => allResults.map((result) => ({
    emailNotifications: result.emailNotifications,
    organisation: result.organisation,
    standInId: result.standInId,
    systemNotifications: result.systemNotifications,
    userId: result.userId,
    name: result.userInfo,
    rolesTableFormat: result.roles?.map((role) => backFormatRoleToCleanFormat(role)).join(' | '),
    roles: result.roles,
  }));

  const userStandInsTableFormat = tableFormat(userStandIns);

  const backFormat = (allResults: any[]) => allResults.map((result) => ({
    emailNotifications: result.emailNotifications,
    organisation: result.organisation,
    standInId: result.standInId,
    systemNotifications: result.systemNotifications,
    userInfo: result.name,
    roles: result.roles,
  }));

  const columns = useMemo<ColumnConfig[]>(() => [
    { name: 'name', title: t('standin.userInfo'), width: 450 },
    {
      name: 'systemNotifications',
      title: t('standin.systemNotifications'),
      width: 300,
      cellRenderer: CELL_RENDERER_TYPES.CHECKBOX,
      cellEditor: CELL_EDITOR_TYPES.CHECKBOX,
      align: ALIGN.CENTER,
      editable: true,
    },
    {
      name: 'emailNotifications',
      title: t('standin.emailNotifications'),
      width: 300,
      cellRenderer: CELL_RENDERER_TYPES.CHECKBOX,
      cellEditor: CELL_EDITOR_TYPES.CHECKBOX,
      align: ALIGN.CENTER,
      editable: true,
    },
    { name: 'rolesTableFormat', title: t('standin.roles'), flex: 1, wrapText: true },
  ], [t]);

  useEffect(() => {
    titleService.updatePageTitle(t('standin.settings'), t('standin.standin'));

    return () => setStandInResalted(false);
  }, [t, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveStandInPopupOff = () => {
    const userSettings = {
      ...userInfo.configuration,
      readStandIn: true,
      ...filtersConfig,
    };

    usersServices.saveUsersConfig(authService?.userCode, userSettings)
      .then(() => setStandInResalted(false))
      .catch((error) => addToast(error.message, 'ERROR'))
      .finally(() => setLoading(false));
  };

  const deleteRows = (standInIds: string[]) => {
    const rowsDelete = userStandInsTableFormat.filter((result) => !standInIds.includes(result.standInId));
    setUserStandInsR(backFormat(rowsDelete));
    setSelection([]);
    setLoading(false);
  };

  const handleDeleteRow = (standInIds: string[]) => {
    setLoading(true);
    usersServices.deleteStandIns(authService?.userCode, standInIds)
      .then(() => deleteRows(standInIds))
      .catch((error) => addToast(error.message, 'ERROR'))
      .finally(() => setLoading(false));
  };

  // Remove when back include user roles in users from DialogUsers list
  const fetchStandins = useApiRequest(usersServices.getStandIns);

  const saveNewStandIn = (standInsBackFormat) => {
    setLoading(true);
    usersServices.saveStandIns(authService?.userCode, standInsBackFormat[standInsBackFormat.length - 1])
      .then(() => {
        // Remove when back include user roles in users from DialogUsers list
        fetchStandins(authService.userCode).then((res) => {
          setUserStandInsR(res?.standInListSingleResponseByUserId);
        });

        // Enable when back include user roles in users from DialogUsers list
        // setUserStandInsR(standInsBackFormat);
        if (!userInfo.configuration?.readStandIn) saveStandInPopupOff();
      })
      .catch((error) => {
        if (error?.code in ERROR_TYPES_ADD_STANDIN) {
          addToast(t(`standin.${error.code}`, { role: errorRoleToFormatedRole?.[error?.message] ?? '' }), 'ERROR');
        } else {
          addToast(error.message, 'ERROR');
        }
      })
      .finally(() => setLoading(false));
  };

  const handleEditRow = (event: CellValueChangedEvent) => {
    const { data: rowData, colDef: { field }, newValue } = event;

    if (!rowData || !field) return;

    setLoading(true);
    const rowEdited = {
      ...rowData,
      [field]: newValue,
    };
    const changedRows = userStandInsTableFormat.map((current) => (current.standInId === rowEdited.standInId ? rowEdited : current));

    usersServices.editStandIn(authService?.userCode, rowEdited)
      .then(() => {
        setUserStandInsR(backFormat(changedRows));
      })
      .catch((error) => {
        addToast(error.message, 'ERROR');
      })
      .finally(() => setLoading(false));
  };

  const addNewStandIn = (userSelected: any) => {
    let rows: any[] = [];
    const data = {
      name: formatSummaryUser(userSelected),
      systemNotifications: false,
      emailNotifications: false,
      userId: authService?.userCode,
      organisation: userSelected.organisation,
      standInId: userSelected.userCode,
      roles: userSelected.rolesList,
    };

    // if the user isnot already selected
    if (!userStandInsTableFormat.find((element) => element.standInId === data.standInId)) {
      rows = [...userStandInsTableFormat];
      rows.push(data);
      saveNewStandIn(backFormat(rows));
    }
  };

  return (
    <>
      <div className={styles.PageContent}>
        <div className={styles.ButtonsContentTable}>
          <IcoButtonSimple
            id="btn-delete-standIn"
            disabled={!(selection.length > 0)}
            icon="icon-muelleimer-loeschen"
            tooltipText={t('dialog.delete')}
            handleClick={() => handleDeleteRow(selection)}
          />
          <SimpleButton
            id="btn-add-standIn"
            color="primary"
            buttonText={t('standin.addStandin')}
            handleClick={() => setNewStandInOpen(true)}
            featured={standInResalted}
            featuredIntermittence={standInResalted}
          />
        </div>
        <TableListEditableNew
          list={userStandInsTableFormat}
          columns={columns}
          columnId="standInId"
          hasCheckbox
          selection={selection}
          onSelectionChange={(selectedRows) => setSelection(selectedRows as string[])}
          onCellValueChanged={handleEditRow}
          loading={loading}
        />
      </div>
      {isNewStandInOpen
        && (
          <DialogUsers
            title={t('standin.standin')}
            close={() => setNewStandInOpen(false)}
            updateUserSelected={(userSelected: any) => addNewStandIn(userSelected)}
            noResultsMessage="standin.noResults"
          />
        )}
    </>
  );
};

const mapStateToProps = (state: any) => ({
  userInfo: getUserAll(state),
  settingsInfo: getSettingsInfo(state),
  standInResalted: getStandInFirstTimeResalted(state),
  filtersConfig: getFiltersConfig(state),
  userStandIns: getStandins(state),
});

const actionCreators = {
  setStandInResalted: setStandInFirstTimeResalted,
  setUserStandInsR: setUserStandIns,
};

export default connect(mapStateToProps, actionCreators)(StandIn);
